import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import { getCustomSdk } from '@/sdk';
import type { Site } from '@/sdk/model/site';
import type { LearningProgram } from '@/sdk/model/learning-program';
import { GroupStatus, type Group } from '@/sdk/model/group';
import type { Teacher } from '@/sdk/model/teacher';
import type { ContextDropdown } from '@/routes';

const STORAGE_KEY = 'ctx-toolbar-selection';

interface SavedToolbarState {
    siteId?: string | null;
    programId?: string | null;
    confirmedGroupId?: string | null;
    unconfirmedGroupId?: string | null;
    allGroupId?: string | null;
    groupId?: string | null; // legacy — migrated on first restore
    teacherId?: string | null;
    pinnedSite?: boolean;
    pinnedProgram?: boolean;
    pinnedGroup?: boolean;
    pinnedTeacher?: boolean;
}

export const useContextToolbarStore = defineStore('contextToolbar', () => {
    const sdk = getCustomSdk();

    // ─── Data lists ───────────────────────────────────────────────────────────
    const sites = ref<Site[]>([]);
    const programs = ref<LearningProgram[]>([]);
    const groups = ref<Group[]>([]);
    const teachers = ref<Teacher[]>([]);

    // ─── Loading states ────────────────────────────────────────────────────────
    const loadingSites = ref(false);
    const loadingPrograms = ref(false);
    const loadingGroups = ref(false);
    const loadingTeachers = ref(false);

    // ─── Selected values ───────────────────────────────────────────────────────
    const selectedSite = ref<Site | null>(null);
    const selectedProgram = ref<LearningProgram | null>(null);
    const selectedGroupConfirmed = ref<Group | null>(null);
    const selectedGroupUnconfirmed = ref<Group | null>(null);
    const selectedGroupAll = ref<Group | null>(null);
    const selectedTeacher = ref<Teacher | null>(null);

    // ─── Pin states (dropdown → label) ────────────────────────────────────────
    const pinnedSite = ref(false);
    const pinnedProgram = ref(false);
    const pinnedGroup = ref(false);
    const pinnedTeacher = ref(false);

    /** Whether the initial data load has completed at least once. */
    const initialized = ref(false);

    /** Dropdowns currently pulsing to attract user attention. Set by individual pages. */
    const highlight = ref<ContextDropdown[]>([]);

    /** Status filter for the group dropdown. Drives which slot selectedGroup reads/writes. */
    const groupStatusFilter = ref<GroupStatus | 'all'>(GroupStatus.Confirmed);

    /** Active group selection — reads/writes the slot matching the current groupStatusFilter. */
    const selectedGroup = computed({
        get: () => {
            if (groupStatusFilter.value === 'all') return selectedGroupAll.value;
            return groupStatusFilter.value === GroupStatus.Confirmed
                ? selectedGroupConfirmed.value
                : selectedGroupUnconfirmed.value;
        },
        set: (val: Group | null) => {
            if (groupStatusFilter.value === 'all') {
                selectedGroupAll.value = val;
            } else if (groupStatusFilter.value === GroupStatus.Confirmed) {
                selectedGroupConfirmed.value = val;
            } else {
                selectedGroupUnconfirmed.value = val;
            }
        },
    });

    /** Flag set during localStorage restore to suppress cascade resets. */
    let isRestoring = false;

    // ─── Computed filtered lists ────────────────────────────────────────────────

    /** Programs filtered by the selected site (or all loaded programs if a site is selected). */
    const filteredPrograms = computed<LearningProgram[]>(() => {
        if (!selectedSite.value) return programs.value;
        return programs.value.filter(p =>
            p.sites.some(s => s.id === selectedSite.value!.id)
        );
    });

    /** Non-archived groups filtered by the selected program and status. */
    const filteredGroups = computed<Group[]>(() => {
        const active = groups.value.filter(g => !g.archived);
        const statusFiltered = groupStatusFilter.value === 'all'
            ? active
            : active.filter(g => g.status === groupStatusFilter.value);
        if (!selectedProgram.value) return statusFiltered;
        return statusFiltered.filter(g => g.learningProgramId === selectedProgram.value!.id);
    });

    /** Teachers filtered by the selected group / program. Populated via loadTeachers(). */
    const filteredTeachers = computed<Teacher[]>(() => teachers.value);

    // ─── Data loading ──────────────────────────────────────────────────────────

    async function loadSites() {
        loadingSites.value = true;
        try {
            const res = await sdk.getSites();
            sites.value = (res?.sites ?? []).filter(s => !s.archived);
        } catch (e) {
            console.error('Failed to load sites', e);
        } finally {
            loadingSites.value = false;
        }
    }

    async function loadPrograms() {
        loadingPrograms.value = true;
        try {
            const res = selectedSite.value
                ? await sdk.learningPrograms()
                : await sdk.availableLearningPrograms();
            programs.value = res?.programs ?? [];
        } catch (e) {
            console.error('Failed to load programs', e);
        } finally {
            loadingPrograms.value = false;
        }
    }

    async function loadGroups() {
        loadingGroups.value = true;
        try {
            const res = await sdk.groups();
            groups.value = res?.groups ?? [];
        } catch (e) {
            console.error('Failed to load groups', e);
        } finally {
            loadingGroups.value = false;
        }
    }

    /** Tracks the latest loadTeachers call to discard stale responses. */
    let teacherLoadSeq = 0;

    /**
     * Loads teachers scoped to the current selection:
     * - selected group or program → all non-archived teachers for that program
     * - nothing selected → all non-archived teachers
     */
    async function loadTeachers() {
        const seq = ++teacherLoadSeq;
        loadingTeachers.value = true;
        try {
            let result: Teacher[];

            const programId = selectedGroup.value?.learningProgramId ?? selectedProgram.value?.id;
            if (programId) {
                const res = await sdk.teachersByProgram(programId);
                result = (res?.teachers ?? []).filter(t => !t.archived);
            } else {
                const res = await sdk.teachers();
                result = (res?.teachers ?? []).filter(t => !t.archived);
            }

            // Discard stale responses caused by rapid selection changes.
            if (seq === teacherLoadSeq) {
                teachers.value = result;
            }
        } catch (e) {
            if (seq === teacherLoadSeq) {
                console.error('Failed to load teachers', e);
            }
        } finally {
            if (seq === teacherLoadSeq) {
                loadingTeachers.value = false;
            }
        }
    }

    /** Persists selected IDs and pin states to localStorage. */
    function saveToStorage() {
        const state: SavedToolbarState = {
            siteId: selectedSite.value?.id ?? null,
            programId: selectedProgram.value?.id ?? null,
            confirmedGroupId: selectedGroupConfirmed.value?.id ?? null,
            unconfirmedGroupId: selectedGroupUnconfirmed.value?.id ?? null,
            allGroupId: selectedGroupAll.value?.id ?? null,
            teacherId: selectedTeacher.value?.id ?? null,
            pinnedSite: pinnedSite.value,
            pinnedProgram: pinnedProgram.value,
            pinnedGroup: pinnedGroup.value,
            pinnedTeacher: pinnedTeacher.value,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }

    /**
     * Restores selections from localStorage after all data has been loaded.
     * Skips cascade resets while restoring so all selections are applied together.
     */
    async function restoreFromStorage() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const saved: SavedToolbarState = JSON.parse(raw);

            isRestoring = true;

            if (saved.siteId) {
                const site = sites.value.find(s => s.id === saved.siteId);
                if (site) selectedSite.value = site;
            }
            if (saved.programId) {
                const program = filteredPrograms.value.find(p => p.id === saved.programId);
                if (program) selectedProgram.value = program;
            }
            const confirmedGroupId = saved.confirmedGroupId ?? saved.groupId;
            if (confirmedGroupId) {
                const group = groups.value
                    .filter(g => !g.archived && g.status === GroupStatus.Confirmed)
                    .find(g => g.id === confirmedGroupId);
                if (group) selectedGroupConfirmed.value = group;
            }
            if (saved.unconfirmedGroupId) {
                const group = groups.value
                    .filter(g => !g.archived && g.status === GroupStatus.Unconfirmed)
                    .find(g => g.id === saved.unconfirmedGroupId);
                if (group) selectedGroupUnconfirmed.value = group;
            }
            if (saved.allGroupId) {
                const group = groups.value
                    .filter(g => !g.archived)
                    .find(g => g.id === saved.allGroupId);
                if (group) selectedGroupAll.value = group;
            }
            pinnedSite.value = saved.pinnedSite ?? false;
            pinnedProgram.value = saved.pinnedProgram ?? false;
            pinnedGroup.value = saved.pinnedGroup ?? false;
            pinnedTeacher.value = saved.pinnedTeacher ?? false;

            // Let queued watchers run while isRestoring is still true.
            await nextTick();
            isRestoring = false;

            if (saved.teacherId) {
                await loadTeachers();
                const teacher = teachers.value.find(t => t.id === saved.teacherId);
                if (teacher) selectedTeacher.value = teacher;
            }
        } catch {
            isRestoring = false;
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    /** Load all reference data needed by the toolbar. */
    async function initialize() {
        await Promise.all([loadSites(), loadPrograms(), loadGroups()]);
        await loadTeachers();
        await restoreFromStorage();
        initialized.value = true;
        saveToStorage(); // Overwrite storage with the actually-valid restored state.
    }

    // ─── Cascading watchers ────────────────────────────────────────────────────

    watch(selectedSite, async () => {
        const skipCascade = isRestoring;
        // Reload programs with the correct scope (available-only vs all for a site).
        await loadPrograms();
        // Reset program if it no longer belongs to the newly selected site.
        if (!skipCascade && selectedSite.value && selectedProgram.value) {
            const stillValid = selectedProgram.value.sites.some(
                s => s.id === selectedSite.value!.id
            );
            if (!stillValid) {
                selectedProgram.value = null;
            }
        }
    });

    watch(selectedProgram, async () => {
        const skipCascade = isRestoring;
        if (!skipCascade) {
            selectedGroupConfirmed.value = null;
            selectedGroupUnconfirmed.value = null;
            selectedTeacher.value = null;
        }
        await loadTeachers();
    });

    watch(selectedGroup, async () => {
        const skipCascade = isRestoring;
        if (!skipCascade) {
            selectedTeacher.value = null;
        }
        await loadTeachers();
    });

    // ─── Persist to localStorage on any selection or pin change ───────────────
    watch(
        [selectedSite, selectedProgram, selectedGroupConfirmed, selectedGroupUnconfirmed, selectedGroupAll, selectedTeacher, pinnedSite, pinnedProgram, pinnedGroup, pinnedTeacher],
        () => {
            if (!isRestoring && initialized.value) saveToStorage();
        }
    );
    /**
     * Switches the group status filter and restores the saved group selection for the new
     * filter without triggering the cascade watcher (teacher is preserved).
     */
    async function switchGroupStatusFilter(newFilter: GroupStatus | 'all') {
        if (newFilter === groupStatusFilter.value) return;
        isRestoring = true;
        groupStatusFilter.value = newFilter;
        await nextTick();
        isRestoring = false;
    }
    // ─── Public API ────────────────────────────────────────────────────────────
    return {
        // Lists
        sites,
        programs,
        groups,
        teachers,
        filteredPrograms,
        filteredGroups,
        filteredTeachers,
        // Loading
        loadingSites,
        loadingPrograms,
        loadingGroups,
        loadingTeachers,
        // Selection
        selectedSite,
        selectedProgram,
        selectedGroup,
        selectedTeacher,
        // Pin state
        pinnedSite,
        pinnedProgram,
        pinnedGroup,
        pinnedTeacher,
        // Group status filter
        groupStatusFilter,
        // Highlight
        highlight,
        // Initialization
        initialized,
        // Actions
        loadSites,
        loadPrograms,
        loadGroups,
        loadTeachers,
        switchGroupStatusFilter,
        initialize,
    };
});
